import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Shell } from "@/components/layout/Shell";
import { useModulos } from "@/hooks/use-modulos";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, HelpCircle, Loader2, XCircle } from "lucide-react";
import { supabase } from "@/lib/supabase";

type RetornoStatus = "verificando" | "aprovado" | "recusado" | "sem_referencia";

export default function RetornoCreditos() {
  const { modulos, isLoading: modulosLoading } = useModulos();
  const [, setLocation] = useLocation();
  const [status, setStatus] = useState<RetornoStatus>("verificando");
  const [compraId, setCompraId] = useState<string>("");

  useEffect(() => {
    if (!modulosLoading && !modulos.creditosPrePagos) {
      setLocation("/dashboard");
    }
  }, [modulosLoading, modulos.creditosPrePagos]);

  useEffect(() => {
    if (modulosLoading || !modulos.creditosPrePagos) return;

    const compraIdFromStorage = sessionStorage.getItem('ultima_compra_id');
    if (!compraIdFromStorage) {
      setStatus("sem_referencia");
      return;
    }

    setCompraId(compraIdFromStorage);
    verificarCreditos(compraIdFromStorage);
  }, [modulosLoading, modulos.creditosPrePagos]);

  const verificarCreditos = async (extRef: string | null) => {
    if (!extRef) {
      setStatus("aprovado");
      return;
    }

    // Aguardar até 10s para o webhook processar
    for (let i = 0; i < 5; i++) {
      await new Promise((r) => setTimeout(r, 2000));

      const { data } = await supabase
        .from("compras_credito")
        .select("status")
        .eq("id", extRef)
        .single();

      if (data?.status === "pago") {
        setStatus("aprovado");
        return;
      }
      if (data?.status === "falhou") {
        setStatus("recusado");
        return;
      }
    }

    // 10s sem confirmação (nem "pago" nem "falhou") — não sabemos o resultado real,
    // então não afirmamos sucesso: cai no estado honesto "não foi possível verificar"
    // (ponytail: evita o fallback otimista anterior, que dizia "Aprovado!" mesmo sem confirmação).
    setStatus("sem_referencia");
  };

  return (
    <Shell>
      <div className="max-w-md mx-auto py-12">
        <Card>
          <CardHeader>
            <CardTitle className="text-center">Pagamento</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {status === "verificando" && (
              <div className="text-center space-y-4 py-4">
                <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto" />
                <p className="font-medium">Verificando pagamento...</p>
                <p className="text-sm text-muted-foreground">
                  Aguarde enquanto confirmamos seu pagamento.
                </p>
              </div>
            )}

            {status === "aprovado" && (
              <div className="text-center space-y-4 py-4">
                <div className="flex justify-center">
                  <div className="rounded-full bg-green-100 p-4">
                    <CheckCircle2 className="w-12 h-12 text-green-600" />
                  </div>
                </div>
                <div>
                  <h2 className="text-xl font-bold">Pagamento Aprovado!</h2>
                  <p className="text-muted-foreground mt-2">
                    Seus créditos foram ativados e já estão disponíveis.
                  </p>
                </div>
                {compraId && (
                  <p className="text-xs text-muted-foreground font-mono">
                    ID: {compraId}
                  </p>
                )}
                <Button onClick={() => setLocation("/creditos")} className="w-full">
                  Ver meus créditos
                </Button>
              </div>
            )}

            {status === "recusado" && (
              <div className="text-center space-y-4 py-4">
                <div className="flex justify-center">
                  <div className="rounded-full bg-red-100 p-4">
                    <XCircle className="w-12 h-12 text-red-600" />
                  </div>
                </div>
                <div>
                  <h2 className="text-xl font-bold">Pagamento não aprovado</h2>
                  <p className="text-muted-foreground mt-2">
                    O pagamento não foi confirmado. Nenhum crédito foi adicionado.
                  </p>
                </div>
                {compraId && (
                  <p className="text-xs text-muted-foreground font-mono">
                    ID: {compraId}
                  </p>
                )}
                <Button
                  onClick={() => setLocation("/creditos/comprar")}
                  className="w-full"
                >
                  Tentar novamente
                </Button>
              </div>
            )}

            {status === "sem_referencia" && (
              <div className="text-center space-y-4 py-4">
                <div className="flex justify-center">
                  <div className="rounded-full bg-slate-100 p-4">
                    <HelpCircle className="w-12 h-12 text-slate-500" />
                  </div>
                </div>
                <div>
                  <h2 className="text-xl font-bold">Não foi possível verificar</h2>
                  <p className="text-muted-foreground mt-2">
                    Não foi possível verificar esta compra automaticamente.
                    Confira seu saldo de créditos ou o histórico de compras.
                  </p>
                </div>
                <Button onClick={() => setLocation("/creditos")} className="w-full">
                  Ver meus créditos
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Shell>
  );
}
